import {
  Button,
  Group,
  Stack,
  Title,
  Paper,
  Table,
  ActionIcon,
  Menu,
  Text,
  useMantineTheme,
  Loader,
  Center,
} from "@mantine/core";
import { Collapse } from "@mantine/core";
import { IconPlus, IconDots, IconEdit, IconTrash } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { WorkStep, WorkStepForm, ObjectData } from "../types/ObjectFormTypes";
import AddEditWorkStepModal from "./modals/AddEditWorkStepModal";
import { useEffect, useState } from "react";
import { modals } from "@mantine/modals";
import {
  getWorkPhaseNames,
  getAllWorkPhaseItems,
  getAllOtherWorkPhaseItems,
  deleteWorkItem,
} from "../../../services/objectmodelservice";
import { WorkPhaseItemDto } from "../../../types/WorkPhaseItem";
import usePermissions from "../../../hooks/usePermissions";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";

interface PlannedWorkStepsTabProps {
  workSteps: WorkStep[];
  setWorkSteps: (steps: WorkStep[]) => void;
  onSubmit: (values: ObjectData) => void;
  form: any; // Will be typed more specifically
  canModify?: boolean;
}

export default function PlannedWorkStepsTab({
  workSteps,
  setWorkSteps,
  onSubmit,
  form,
  canModify = true,
}: PlannedWorkStepsTabProps) {
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem,
  );
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({
    pageId: 306,
    addressSystemId: addrIdFromStore || 0,
    autoLoad: true,
  });
  const [workStepModalOpened, setWorkStepModalOpened] = useState(false);
  const [editingWorkStep, setEditingWorkStep] = useState<WorkStep | null>(null);
  const [editingFetchedItem, setEditingFetchedItem] =
    useState<WorkPhaseItemDto | null>(null);
  const [isEditingOther, setIsEditingOther] = useState<boolean>(false);
  const [groupName, setGroupName] = useState<string>("");
  const [items, setItems] = useState<WorkPhaseItemDto[]>([]);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const { checkAccess } = usePermissions();
  const canModifyWorkSteps = canModify && checkAccess("WORK_PLANNED_MODIFY");

  const [loading, setLoading] = useState(false);
  // Ensure consistent column widths between outer header and inner collapse tables
  const renderColGroup = () => (
    <colgroup>
      <col style={{ width: "28%" }} />
      <col style={{ width: "10%" }} />
      <col style={{ width: "9%" }} />
      <col style={{ width: "13%" }} />
      <col style={{ width: "9%" }} />
      <col style={{ width: "11%" }} />
      <col style={{ width: "13%" }} />
      <col style={{ width: "7%" }} />
    </colgroup>
  );
  const theme = useMantineTheme();
  const headerColor = theme.colors.gray[2];
  const headerText = theme.colors.dark[7];

  // Subscribe to refresh trigger for cross-tab sync
  const workItemsRefreshTrigger = useTransientStore(
    (state) => state.workItemsRefreshTrigger,
  );

  // Derive IDs from the shared form; fall back to fields used in InformationTab
  // contractId often comes from selected job (string id) in InformationTab
  const contractId: number | undefined = (() => {
    const explicit = form?.values?.contractId;
    if (typeof explicit === "number" && explicit > 0) return explicit;
    const job = form?.values?.job; // string id from Select
    if (typeof job === "string" && job.length > 0) {
      const parsed = parseInt(job, 10);
      return Number.isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
  })();

  // objectId may be stored as `objectId` or the main object `id` in InformationTab
  const objectId: number | undefined = (() => {
    const explicit = form?.values?.objectId;
    if (typeof explicit === "number" && explicit > 0) return explicit;
    const objId = form?.values?.id;
    if (typeof objId === "number" && objId > 0) return objId;
    return undefined;
  })();

  const providedGroupId: number | undefined = (() => {
    const g = form?.values?.generalPriceGroupId;
    if (typeof g === "number" && g > 0) return g;
    return undefined;
  })();

  // Optional: allow selecting multiple explicit groups via form
  const providedGroupIds: number[] | undefined = (() => {
    const arr = form?.values?.generalPriceGroupIds;
    if (Array.isArray(arr)) {
      const nums = arr
        .map((x: any) => (typeof x === "string" ? parseInt(x, 10) : x))
        .filter((n: any) => typeof n === "number" && n > 0);
      return nums.length ? nums : undefined;
    }
    return undefined;
  })();

  // Derive a human-friendly job name for the header
  // const jobHeader: string | undefined = (() => {
  //   const v = form?.values ?? {};
  //   // Prefer explicit name fields set by InformationTab
  //   const nameCandidates = [
  //     v.jobName,
  //     v.jobDisplayName,
  //     v.jobLabel,
  //     v.selectedJobName,
  //   ].filter((x: any) => typeof x === "string" && x.trim().length > 0);
  //   if (nameCandidates.length) return nameCandidates[0];
  //   // If only id is available, avoid showing raw id as name
  //   // Fall back to contractName if present
  //   if (typeof v.contractName === "string" && v.contractName.trim().length)
  //     return v.contractName;
  //   return undefined;
  // })();

  useEffect(() => {
    const loadData = async () => {
      // console.log("data1: ", {
      //   contractId,
      //   objectId,
      //   providedGroupId,
      //   job: form?.values?.job,
      //   id: form?.values?.id,
      // });
      if (!contractId || !objectId) return;
      setLoading(true);
      // Fetch group list, then fetch items for multiple groups (combine)
      const groups = await getWorkPhaseNames(contractId, objectId, 0);
      // If no operation groups exist, still load "Others" so new entries are visible
      if (!groups?.length) {
        const othersRows = (
          await getAllOtherWorkPhaseItems(contractId, objectId, 0)
        )?.filter((r) => typeof r.workItemId === "number" && r.workItemId > 0);
        const combinedWithHeaders: WorkPhaseItemDto[] = [];
        combinedWithHeaders.push({
          workItemId: null as any,
          itemCode: "",
          name: "Others",
          unitName: "",
          unitPrice: 0,
          itemCount: 0,
          itemArea: undefined,
          itemWeight: undefined,
          totalPrice: 0,
        } as WorkPhaseItemDto);
        combinedWithHeaders.push(...(othersRows ?? []));
        setGroupName("Others");
        setItems(combinedWithHeaders);
        setOpenGroups({ Others: true });
        setLoading(false);
        return;
      }
      // Choose target groups:
      // - If multiple explicit IDs provided, use them
      // - Else if single providedGroupId, use it
      // - Else include ALL groups
      const targetGroupIds: number[] = providedGroupIds
        ? providedGroupIds
        : providedGroupId
          ? [providedGroupId]
          : groups.map((g) => g.id);

      // Set header name to combined view for clarity
      setGroupName(
        targetGroupIds
          .map((id) => groups.find((g) => g.id === id)?.name)
          .filter(Boolean)
          .join(" + "),
      );

      const allRowsArrays = await Promise.all([
        ...targetGroupIds.map((gid) =>
          getAllWorkPhaseItems(gid, contractId, objectId, 0),
        ),
        getAllOtherWorkPhaseItems(contractId, objectId, 0),
      ]);
      // Build combined list with a single synthetic header per group,
      // and filter out any backend-provided header rows to avoid duplicates.
      const combinedWithHeaders: WorkPhaseItemDto[] = [];
      targetGroupIds.forEach((gid, idx) => {
        const gname = groups.find((g) => g.id === gid)?.name ?? "";
        combinedWithHeaders.push({
          workItemId: null as any,
          itemCode: "",
          name: gname,
          unitName: "",
          unitPrice: 0,
          itemCount: 0,
          itemArea: undefined,
          itemWeight: undefined,
          totalPrice: 0,
        } as WorkPhaseItemDto);
        const rows = (allRowsArrays[idx] ?? []).filter(
          (r) => typeof r.workItemId === "number" && r.workItemId > 0,
        );
        combinedWithHeaders.push(...rows);
      });
      // Append Others group if there is data
      const othersRows = (allRowsArrays[allRowsArrays.length - 1] ?? []).filter(
        (r) => typeof r.workItemId === "number" && r.workItemId > 0,
      );
      if (othersRows.length > 0) {
        combinedWithHeaders.push({
          workItemId: null as any,
          itemCode: "",
          name: "Others",
          unitName: "",
          unitPrice: 0,
          itemCount: 0,
          itemArea: undefined,
          itemWeight: undefined,
          totalPrice: 0,
        } as WorkPhaseItemDto);
        combinedWithHeaders.push(...othersRows);
      }
      setItems(combinedWithHeaders);
      // Initialize all groups as expanded by default
      const groupNames = combinedWithHeaders
        .filter((item) => item.workItemId == null)
        .map((item) => item.name);
      const initialOpenState = Object.fromEntries(
        groupNames.map((name) => [name, true]),
      );
      setOpenGroups(initialOpenState);
      setLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, objectId, providedGroupId, workItemsRefreshTrigger]);

  const reloadItems = async () => {
    if (!contractId || !objectId) return;
    setLoading(true);
    const groups = await getWorkPhaseNames(contractId, objectId, 0);
    // Handle empty group list by loading "Others" items so the table is not blank
    if (!groups?.length) {
      const othersRows = (
        await getAllOtherWorkPhaseItems(contractId, objectId, 0)
      )?.filter((r) => typeof r.workItemId === "number" && r.workItemId > 0);
      const combinedWithHeaders: WorkPhaseItemDto[] = [];
      combinedWithHeaders.push({
        workItemId: null as any,
        itemCode: "",
        name: "Others",
        unitName: "",
        unitPrice: 0,
        itemCount: 0,
        itemArea: undefined,
        itemWeight: undefined,
        totalPrice: 0,
      } as WorkPhaseItemDto);
      combinedWithHeaders.push(...(othersRows ?? []));
      setGroupName("Others");
      setItems(combinedWithHeaders);
      setOpenGroups({ Others: true });
      setLoading(false);
      return;
    }
    const targetGroupIds: number[] = providedGroupIds
      ? providedGroupIds
      : providedGroupId
        ? [providedGroupId]
        : groups.map((g) => g.id);

    setGroupName(
      targetGroupIds
        .map((id) => groups.find((g) => g.id === id)?.name)
        .filter(Boolean)
        .join(" + "),
    );

    const allRowsArrays = await Promise.all([
      ...targetGroupIds.map((gid) =>
        getAllWorkPhaseItems(gid, contractId, objectId, 0),
      ),
      getAllOtherWorkPhaseItems(contractId, objectId, 0),
    ]);
    // Build combined list with a single synthetic header per group,
    // and filter out any backend-provided header rows to avoid duplicates.
    const combinedWithHeaders: WorkPhaseItemDto[] = [];
    targetGroupIds.forEach((gid, idx) => {
      const gname = groups.find((g) => g.id === gid)?.name ?? "";
      combinedWithHeaders.push({
        workItemId: null as any,
        itemCode: "",
        name: gname,
        unitName: "",
        unitPrice: 0,
        itemCount: 0,
        itemArea: undefined,
        itemWeight: undefined,
        totalPrice: 0,
      } as WorkPhaseItemDto);
      const rows = (allRowsArrays[idx] ?? []).filter(
        (r) => typeof r.workItemId === "number" && r.workItemId > 0,
      );
      combinedWithHeaders.push(...rows);
    });
    const othersRows = (allRowsArrays[allRowsArrays.length - 1] ?? []).filter(
      (r) => typeof r.workItemId === "number" && r.workItemId > 0,
    );
    if (othersRows.length > 0) {
      combinedWithHeaders.push({
        workItemId: null as any,
        itemCode: "",
        name: "Others",
        unitName: "",
        unitPrice: 0,
        itemCount: 0,
        itemArea: undefined,
        itemWeight: undefined,
        totalPrice: 0,
      } as WorkPhaseItemDto);
      combinedWithHeaders.push(...othersRows);
    }
    setItems(combinedWithHeaders);
    // Initialize all groups as expanded by default
    const groupNames = combinedWithHeaders
      .filter((item) => item.workItemId == null)
      .map((item) => item.name);
    const initialOpenState = Object.fromEntries(
      groupNames.map((name) => [name, true]),
    );
    setOpenGroups(initialOpenState);
    setLoading(false);
  };

  const handleAddWorkStep = () => {
    // Only allow adding if backend items exist to source operations
    // if (!items.length) {
    //   modals.openConfirmModal({
    //     title: "No operations available",
    //     children: (
    //       <Text size="sm">
    //         No operation items were found for this contract/object.
    //       </Text>
    //     ),
    //     labels: { confirm: "Ok", cancel: "" },
    //     cancelProps: { display: "none" },
    //     onConfirm: () => {},
    //   });
    //   return;
    // }
    setEditingWorkStep(null);
    setWorkStepModalOpened(true);
  };

  const handleEditWorkStep = (workStep: WorkStep) => {
    setEditingWorkStep(workStep);
    setWorkStepModalOpened(true);
  };

  const mapFetchedItemToWorkStep = (it: WorkPhaseItemDto): WorkStep => {
    return {
      id: it.workItemId,
      typeOfCoating: it.name,
      unit: it.unitName,
      amount: it.itemCount ?? 0,
      surfaceArea: it.itemArea ?? 0,
      tons: it.itemWeight ?? 0,
      unitPrice: Number(it.unitPrice ?? 0),
      priceC: Number(it.totalPrice ?? 0),
    };
  };

  const handleEditFetchedItem = (it: WorkPhaseItemDto) => {
    setEditingFetchedItem(it);
    // Determine if the clicked item belongs to the "Others" group by scanning back to the nearest header
    const idx = items.findIndex((x) => x.workItemId === it.workItemId);
    let groupHeaderName: string | null = null;
    for (let i = idx - 1; i >= 0; i--) {
      const row = items[i];
      if (row.workItemId == null) {
        groupHeaderName = row.name;
        break;
      }
    }
    setIsEditingOther(groupHeaderName === "Others");
    setEditingWorkStep(mapFetchedItemToWorkStep(it));
    setWorkStepModalOpened(true);
  };

  const confirmAndDeleteFetchedItem = (workItemId: number) => {
    if (!workItemId) return;
    //console.log("Delete click", { workItemId, contractId, objectId });
    // If required identifiers are missing, show an info modal instead of doing nothing
    if (!contractId || !objectId) {
      modals.openConfirmModal({
        title: getTranslation("TITLE_MISSING_CONTEXT") || "Missing context",
        children: (
          <Text size="sm">
            {getTranslation("MESSAGE_SELECT_CONTRACT_OBJECT_BEFORE_DELETE") ||
              "Contract or Object is not selected. Please select them before deleting items."}
          </Text>
        ),
        labels: {
          confirm: getTranslation("LABEL_OK") || "Ok",
          cancel: "",
        },
        cancelProps: { display: "none" },
        onConfirm: () => {},
      });
      return;
    }
    try {
      modals.openConfirmModal({
        title: getTranslation("TITLE_DELETE_ITEM") || "Delete item",
        children: (
          <Text size="sm">
            {getTranslation("MESSAGE_CONFIRM_DELETE_ITEM") ||
              "Are you sure you want to delete this item?"}
          </Text>
        ),
        labels: {
          confirm: getTranslation("LABEL_DELETE") || "Delete",
          cancel: getTranslation("LABEL_CANCEL") || "Cancel",
        },
        confirmProps: { color: "red" },
        onConfirm: () => {
          deleteWorkItem(workItemId, contractId, objectId)
            .then(() => {
              setItems((prev) =>
                prev.filter((x) => x.workItemId !== workItemId),
              );
              notifications.show({
                title: getTranslation("TITLE_SUCCESS") || "Success",
                message:
                  getTranslation("MESSAGE_WORK_ITEM_DELETED") ||
                  "The work item was deleted successfully.",
                color: "green",
                autoClose: 5000,
                position: "top-center",
                classNames: {
                  root: "notification-success",
                },
              });
              // Refresh from backend to ensure full sync (totals, headers, etc.)
              reloadItems();
              // Trigger refresh for other tabs
              useTransientStore.getState().triggerWorkItemsRefresh();
            })
            .catch((error) => {
              console.error("Failed to delete work item", error);
              const errorMessage =
                error instanceof Error ? error.message : "Unknown error";
              notifications.show({
                title: getTranslation("TITLE_ERROR") || "Error",
                message:
                  getTranslation("MESSAGE_WORK_ITEM_DELETE_FAILED") ||
                  `Failed to delete the work item: ${errorMessage}`,
                color: "red",
                autoClose: 5000,
                position: "top-center",
                classNames: {
                  root: "notification-error",
                },
              });
            });
        },
      });
    } catch (e) {
      // Fallback if Mantine ModalsProvider is not mounted
      const ok = window.confirm(
        getTranslation("MESSAGE_CONFIRM_DELETE_ITEM") ||
          "Are you sure you want to delete this item?",
      );
      if (ok) {
        deleteWorkItem(workItemId, contractId, objectId)
          .then(() => {
            setItems((prev) => prev.filter((x) => x.workItemId !== workItemId));
            notifications.show({
              title: getTranslation("TITLE_SUCCESS") || "Success",
              message:
                getTranslation("MESSAGE_WORK_ITEM_DELETED") ||
                "The work item was deleted successfully.",
              color: "green",
              autoClose: 5000,
              position: "top-center",
              classNames: {
                root: "notification-success",
              },
            });
            reloadItems();
            // Trigger refresh for other tabs
            useTransientStore.getState().triggerWorkItemsRefresh();
          })
          .catch((error) => {
            console.error("Failed to delete work item", error);
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";
            notifications.show({
              title: getTranslation("TITLE_ERROR") || "Error",
              message:
                getTranslation("MESSAGE_WORK_ITEM_DELETE_FAILED") ||
                `Failed to delete the work item: ${errorMessage}`,
              color: "red",
              autoClose: 5000,
              position: "top-center",
              classNames: {
                root: "notification-error",
              },
            });
          });
      }
    }
  };

  const handleDeleteWorkStep = (id: number) => {
    const updatedSteps = workSteps.filter((step) => step.id !== id);
    setWorkSteps(updatedSteps);
    // Update the main form
    form.setFieldValue("plannedWorkSteps", updatedSteps);
  };

  const handleSaveWorkStep = (values: WorkStepForm) => {
    const priceC = values.amount * values.surfaceArea * values.tons; // Simple calculation for demo

    if (editingFetchedItem) {
      // Update fetched item row using modal values
      const updatedItems = items.map((it) => {
        if (it.workItemId === editingFetchedItem.workItemId) {
          const newAmount = values.amount ?? 0;
          const newArea = values.surfaceArea ?? 0;
          const newTons = values.tons ?? 0;
          const unitPrice = isEditingOther
            ? (values.otherUnitPrice ?? it.unitPrice ?? 0)
            : (it.unitPrice ?? 0);
          // Recompute total price — use count * unitPrice for row total
          const newTotal = (newAmount ?? 0) * unitPrice;
          return {
            ...it,
            // For Others, update name/unit and unitPrice from the modal values
            name: isEditingOther ? values.anotherName || it.name : it.name,
            unitName: isEditingOther
              ? values.otherUnit || it.unitName
              : it.unitName,
            description: values.description ?? it.description,
            itemCount: newAmount,
            itemArea: newArea,
            itemWeight: newTons,
            unitPrice: unitPrice,
            totalPrice: newTotal,
          };
        }
        return it;
      });
      setItems(updatedItems);
      setWorkStepModalOpened(false);
      setEditingWorkStep(null);
      setEditingFetchedItem(null);
      return;
    }

    if (editingWorkStep) {
      // Update existing work step
      const updatedStep: WorkStep = {
        ...editingWorkStep,
        typeOfCoating: values.operation,
        unit: values.otherUnit,
        amount: values.amount,
        surfaceArea: values.surfaceArea,
        tons: values.tons,
        unitPrice: 100, // Demo price
        priceC: priceC,
      };

      const updatedSteps = workSteps.map((step) =>
        step.id === editingWorkStep.id ? updatedStep : step,
      );
      setWorkSteps(updatedSteps);
      form.setFieldValue("plannedWorkSteps", updatedSteps);
    } else {
      // Add new work step
      const newStep: WorkStep = {
        id: Date.now(), // Simple ID generation for demo
        typeOfCoating: values.operation,
        unit: values.otherUnit,
        amount: values.amount,
        surfaceArea: values.surfaceArea,
        tons: values.tons,
        unitPrice: 100, // Demo price
        priceC: priceC,
      };

      const updatedSteps = [...workSteps, newStep];
      setWorkSteps(updatedSteps);
      form.setFieldValue("plannedWorkSteps", updatedSteps);
    }

    setWorkStepModalOpened(false);
    setEditingWorkStep(null);
    setEditingFetchedItem(null);
    // Refresh grid from backend to reflect latest values
    reloadItems();
    // Trigger refresh for other tabs
    useTransientStore.getState().triggerWorkItemsRefresh();
  };

  const calculateTotalCost = () => {
    return workSteps.reduce((total, step) => total + step.priceC, 0);
  };

  const handleReset = () => {
    setWorkSteps([]);
    form.setFieldValue("plannedWorkSteps", []);
  };

  const handleSave = () => {
    onSubmit(form.getValues());
  };

  return (
    <>
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Title order={4}>{`${
            getTranslation("TITLE_WORK_STEPS") || "Work steps"
          } - ${
            // Prefer explicit jobName set by InformationTab, then contractName; remove object name from title
            form?.values?.jobName && String(form.values.jobName).trim()
              ? String(form.values.jobName).trim()
              : form?.values?.contractName &&
                  String(form.values.contractName).trim()
                ? String(form.values.contractName).trim()
                : getTranslation("LABEL_CONTRACT") || "Contract"
          }`}</Title>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAddWorkStep}
            size="sm"
            disabled={!canModifyWorkSteps || !canModify}
          >
            {getTranslation("LABEL_ADD_NEW") || "Add New"}
          </Button>
        </Group>

        {(!canModifyWorkSteps || !canModify) && (
          <Text c="grey">
            {getTranslation("LABEL_NO_PERMISSION_PLANNED_WORK_STEPS") ||
              "You do not have permission to modify planned work steps."}
          </Text>
        )}

        <Paper withBorder>
          <Table striped highlightOnHover>
            {renderColGroup()}
            <Table.Thead>
              <Table.Tr
                style={{ backgroundColor: headerColor, color: headerText }}
              >
                <Table.Th style={{ color: headerText }}>
                  {getTranslation("LABEL_COATING_TYPE_WORK_PHASE") ||
                    "Type of Coating / Work Phase"}
                </Table.Th>
                <Table.Th
                  style={{ color: headerText, borderLeft: "1px solid #ddd" }}
                >
                  {getTranslation("LABEL_UNIT") || "Unit"}
                </Table.Th>
                <Table.Th
                  style={{ color: headerText, borderLeft: "1px solid #ddd" }}
                >
                  {getTranslation("LABEL_AMOUNT") || "Amount"}
                </Table.Th>
                <Table.Th
                  style={{ color: headerText, borderLeft: "1px solid #ddd" }}
                >
                  {getTranslation("LABEL_SURFACE_AREA") || "Surface Area"}
                </Table.Th>
                <Table.Th
                  style={{ color: headerText, borderLeft: "1px solid #ddd" }}
                >
                  {getTranslation("LABEL_TONS") || "Tons"}
                </Table.Th>
                <Table.Th
                  style={{ color: headerText, borderLeft: "1px solid #ddd" }}
                >
                  {getTranslation("LABEL_UNIT_PRICE") || "Unit Price"}
                </Table.Th>
                <Table.Th
                  style={{ color: headerText, borderLeft: "1px solid #ddd" }}
                >
                  {getTranslation("LABEL_PRICE_EUR") || "Price €"}
                </Table.Th>
                <Table.Th
                  style={{
                    width: "100px",
                    color: headerText,
                    textAlign: "center",
                    borderLeft: "1px solid #ddd",
                  }}
                >
                  {getTranslation("LABEL_ACTIONS") || "Actions"}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td colSpan={8}>
                    <Center py="xl">
                      <Loader size="md" />
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : (
                (() => {
                  const groups: { name: string; rows: WorkPhaseItemDto[] }[] =
                    [];
                  let currentGroup: {
                    name: string;
                    rows: WorkPhaseItemDto[];
                  } | null = null;
                  for (const it of items) {
                    const isHeader = it.workItemId == null;
                    if (isHeader) {
                      if (currentGroup && currentGroup.rows.length)
                        groups.push(currentGroup);
                      currentGroup = { name: it.name, rows: [] };
                      continue;
                    }
                    if (
                      !(typeof it.workItemId === "number" && it.workItemId > 0)
                    )
                      continue;
                    if (!currentGroup) currentGroup = { name: "", rows: [] };
                    currentGroup.rows.push(it);
                  }
                  if (currentGroup && currentGroup.rows.length)
                    groups.push(currentGroup);

                  const toggleGroup = (name: string) => {
                    setOpenGroups((prev) => ({ ...prev, [name]: !prev[name] }));
                  };

                  return groups.flatMap((g, gi) => [
                    <Table.Tr
                      key={`grp-${gi}`}
                      style={{
                        backgroundColor: headerColor,
                        color: headerText,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                      onClick={() => toggleGroup(g.name)}
                    >
                      <Table.Td style={{ color: headerText }}>
                        {openGroups[g.name] ? "▼ " : "► "}
                        {g.name}
                      </Table.Td>
                      <Table.Td style={{ color: headerText }}></Table.Td>
                      <Table.Td style={{ color: headerText }}></Table.Td>
                      <Table.Td style={{ color: headerText }}></Table.Td>
                      <Table.Td style={{ color: headerText }}></Table.Td>
                      <Table.Td style={{ color: headerText }}></Table.Td>
                      <Table.Td style={{ color: headerText }}></Table.Td>
                      <Table.Td style={{ color: headerText }}></Table.Td>
                    </Table.Tr>,
                    <Table.Tr key={`grp-panel-${gi}`}>
                      <Table.Td colSpan={8} style={{ padding: 0 }}>
                        <Collapse in={!!openGroups[g.name]}>
                          <Table striped highlightOnHover>
                            {renderColGroup()}
                            <Table.Tbody>
                              {g.rows.map((it) => (
                                <Table.Tr key={it.workItemId}>
                                  <Table.Td>{it.name}</Table.Td>
                                  <Table.Td
                                    style={{ borderLeft: "1px solid #ddd" }}
                                  >
                                    {it.unitName}
                                  </Table.Td>
                                  <Table.Td
                                    style={{
                                      textAlign: "right",
                                      borderLeft: "1px solid #ddd",
                                    }}
                                  >
                                    {it.itemCount}
                                  </Table.Td>
                                  <Table.Td
                                    style={{
                                      textAlign: "right",
                                      borderLeft: "1px solid #ddd",
                                    }}
                                  >
                                    {it.itemArea ?? 0}
                                  </Table.Td>
                                  <Table.Td
                                    style={{
                                      textAlign: "right",
                                      borderLeft: "1px solid #ddd",
                                    }}
                                  >
                                    {it.itemWeight ?? 0}
                                  </Table.Td>
                                  <Table.Td
                                    style={{
                                      textAlign: "right",
                                      borderLeft: "1px solid #ddd",
                                    }}
                                  >
                                    {it.unitPrice.toFixed(1)}
                                  </Table.Td>
                                  <Table.Td
                                    style={{
                                      textAlign: "right",
                                      borderLeft: "1px solid #ddd",
                                    }}
                                  >
                                    {it.totalPrice.toFixed(2)}
                                  </Table.Td>
                                  <Table.Td
                                    style={{
                                      borderLeft: "1px solid #ddd",
                                      justifyItems: "center",
                                    }}
                                  >
                                    <Group gap="xs">
                                      <ActionIcon
                                        variant="subtle"
                                        aria-label={
                                          getTranslation("ARIA_EDIT") || "Edit"
                                        }
                                        onClick={() =>
                                          handleEditFetchedItem(it)
                                        }
                                        disabled={
                                          !canModify || !canModifyWorkSteps
                                        }
                                      >
                                        <IconEdit size={16} />
                                      </ActionIcon>
                                      <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        aria-label={
                                          getTranslation("ARIA_DELETE") ||
                                          "Delete"
                                        }
                                        onClick={() =>
                                          confirmAndDeleteFetchedItem(
                                            it.workItemId,
                                          )
                                        }
                                        disabled={
                                          !canModify || !canModifyWorkSteps
                                        }
                                      >
                                        <IconTrash size={16} />
                                      </ActionIcon>
                                    </Group>
                                  </Table.Td>
                                </Table.Tr>
                              ))}
                            </Table.Tbody>
                          </Table>
                        </Collapse>
                      </Table.Td>
                    </Table.Tr>,
                  ]);
                })()
              )}
            </Table.Tbody>
            <Table.Tfoot>
              <Table.Tr
                style={{
                  backgroundColor: headerColor,
                  color: headerText,
                  fontWeight: 700,
                }}
              >
                <Table.Td colSpan={6} style={{ color: headerText }}>
                  {getTranslation("LABEL_TOTAL_EUR") || "Total €"}
                </Table.Td>
                <Table.Td
                  style={{
                    textAlign: "right",
                    borderLeft: "1px solid #ddd",
                    color: headerText,
                  }}
                >
                  {items.reduce((sum, it) => sum + it.totalPrice, 0).toFixed(2)}
                </Table.Td>
                <Table.Td
                  style={{ color: headerText, borderLeft: "1px solid #ddd" }}
                ></Table.Td>
              </Table.Tr>
            </Table.Tfoot>
          </Table>
        </Paper>
      </Stack>

      <AddEditWorkStepModal
        opened={workStepModalOpened}
        onClose={() => setWorkStepModalOpened(false)}
        editingWorkStep={editingWorkStep}
        onSave={handleSaveWorkStep}
        contractId={contractId}
        objectId={objectId}
        workItemType={0}
        editingFetchedItem={editingFetchedItem}
        isOtherOperation={isEditingOther}
      />
    </>
  );
}
